import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { NavItem } from '@/config/navigation';
import { usePermissions } from '@/hooks/usePermissions';

interface SidebarNavItemProps {
  item: NavItem;
  collapsed: boolean;
  onClick?: () => void;
}

export function SidebarNavItem({ item, collapsed, onClick }: SidebarNavItemProps) {
  const location = useLocation();
  const pathname = location.pathname;
  // New-tab items never become active here — the current tab doesn't navigate.
  const isActive =
    !item.openInNewTab && (pathname === item.href || pathname.startsWith(`${item.href}/`));
  const Icon = item.icon;
  const { can } = usePermissions();

  if (item.enabled === false) {
    return null;
  }

  // Hide nav items the user doesn't have permission to access
  if (item.permission && !can(item.permission.resource, item.permission.action)) {
    return null;
  }

  return (
    <Link
      to={item.href}
      onClick={onClick}
      {...(item.openInNewTab && { target: '_blank', rel: 'noopener noreferrer' })}
      className={cn(
        'flex items-center rounded-md no-underline transition-colors',
        collapsed ? 'mx-auto h-8 w-10 justify-center' : 'gap-2 px-3 py-1.5',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        'focus-visible:ring-sidebar-ring focus-visible:outline-none focus-visible:ring-2'
      )}
      aria-current={isActive ? 'page' : undefined}
      title={collapsed ? item.label : undefined}
    >
      <Icon className="icon-14 shrink-0" aria-hidden="true" />
      {!collapsed && (
        <>
          <span className="truncate">{item.label}</span>
          {item.badge !== undefined && item.badge > 0 && (
            <span className="bg-sidebar-primary text-sidebar-primary-foreground ml-auto rounded-full px-2 py-0.5 text-xs">
              {item.badge > 99 ? '99+' : item.badge}
            </span>
          )}
        </>
      )}
    </Link>
  );
}
